import {
  setSelectedEndDate,
  setSelectedStartDate,
} from "@/store/project-filtering/project-filtering-slice";
import {
  selectSearchEndDate,
  selectSearchStartDate,
} from "@/store/project-filtering/project-filtering-selectors";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { useCallback, useMemo } from "react";

/** Read/write access to the project-wide capture-date filter */
export type CaptureDateFilter = {
  /** Inclusive start of the active range, undefined when unbounded */
  startDate?: Date;
  /** Inclusive end of the active range, undefined when unbounded */
  endDate?: Date;
  /** True while the date filter constrains the project */
  isActive: boolean;
  /** Replace the active range; called with no argument it removes the filter */
  setRange(start?: Date, end?: Date): void;
  /** Remove the date filter, leaving any text/tag filter untouched */
  clear(): void;
};

/**
 * Binds a control to the same `projectFiltering.dateRange` the project tree's
 * "Time of capture" filter writes to, so both stay in sync by construction.
 *
 * @returns The active capture-date range and the callbacks to change it
 */
export function useCaptureDateFilter(): CaptureDateFilter {
  const dispatch = useAppDispatch();
  const start = useAppSelector(selectSearchStartDate);
  const end = useAppSelector(selectSearchEndDate);

  const startDate = useMemo(
    () => (start ? new Date(start) : undefined),
    [start],
  );
  const endDate = useMemo(() => (end ? new Date(end) : undefined), [end]);

  const setRange = useCallback(
    (newStart?: Date, newEnd?: Date) => {
      // Mirror a one-sided selection, matching the project tree filter's behavior
      if (newStart && newEnd === undefined) newEnd = newStart;
      if (newEnd && newStart === undefined) newStart = newEnd;

      dispatch(setSelectedStartDate(newStart?.toISOString()));
      dispatch(setSelectedEndDate(newEnd?.toISOString()));
    },
    [dispatch],
  );

  // Clears only the dates: `resetTagsAndDates` would also drop the tag filter
  const clear = useCallback(() => setRange(), [setRange]);

  return {
    startDate,
    endDate,
    isActive: !!startDate || !!endDate,
    setRange,
    clear,
  };
}
